/*
 * Staleness and elapsed-time math — CLIENT-SIDE, per contract §0.
 *
 * The server sends `observed_at` and nothing else about freshness: no
 * `is_stale`, no tier, no label. This module is the whole of the decision, so a
 * future native client reimplements one small documented function rather than
 * inferring a policy from scattered comparisons.
 *
 * TWO CALIBRATIONS OF THE SAME AFFORDANCE (§5, §10, §11):
 *
 *   PLANNING (Upcoming, Past) — hours are normal. Phrased as reassurance
 *   ("Checked 3 hours ago"), and NEVER faded or ambered. A registration list
 *   read this morning is not stale; saying so would train the user to ignore
 *   the one signal that matters at a venue.
 *
 *   DAY-OF (Active) — an old reading must read that way IN WORDS: the "as of"
 *   turns amber and the row states the age plainly. NOTHING about the datum or
 *   its layout changes (decisions 454 and 462, against §6 and §9's day-of
 *   calibration) — a strip read twenty minutes ago is still the last thing
 *   FencingTime Live posted, and every treatment that said "trust this less"
 *   also made it harder to read across a sports hall.
 *
 * WHAT THE TIER IS FOR, now that nothing fades: whether the age is STATED
 * (Active's "strip seen N min ago" row and the amber "as of"), and the
 * went-quiet line. It no longer sorts anything: Active orders events by start
 * time alone (amending decision 314), so an aging datum describes its card and
 * never moves it.
 * The returning tab's refetch used to be a third and is deliberately no longer
 * tied to it — see wakeup.ts.
 */

/** The three steps. Named, not numeric, so a comparison cannot be off by one. */
export type StalenessTier = "fresh" | "aging" | "quiet";

/**
 * Day-of thresholds, in minutes.
 *
 * TWENTY, NOT §9'S FIVE (decision 462, Kevin on this cluster's screenshots).
 * Five minutes was measured against nothing: "between rounds" genuinely lasts
 * fifteen or twenty minutes at a reasonably large tournament, a DE round takes
 * longer where an event has few strips per bout, and a fencer with a bye waits
 * out a whole table — so at five minutes the app was ambering readings that
 * were still the current state of the venue. A signal that fires on the
 * ordinary case is a signal a reader learns to ignore, which costs more than
 * having no signal at all.
 *
 * DELIBERATELY A PLACEHOLDER FOR ARITHMETIC WE CANNOT DO YET. The honest
 * threshold is "longer than this reading should plausibly have lasted", which
 * depends on the round kind, the field size, the strips available and whether
 * the fencer had a bye. That is a statistical question about a corpus we are
 * still collecting, and it is recorded as the open trigger on decision 462
 * rather than guessed at with a second number.
 *
 * Thirty is unchanged: the point at which a live event has almost certainly
 * moved on, and the event line says so in words ("updates stopped"). §9's
 * open question about whether a fully-stale event demotes its whole container
 * is now ANSWERED, and answered no (462).
 */
export const DAY_OF_AGING_MINUTES = 20;
export const DAY_OF_QUIET_MINUTES = 30;

export type Regime = "planning" | "day-of";

/** Minutes elapsed between two instants, floored and never negative. */
export function minutesSince(observedAt: string | Date, now: Date = new Date()): number {
  const then = typeof observedAt === "string" ? new Date(observedAt) : observedAt;
  const ms = now.getTime() - then.getTime();
  // A negative age means a clock skew between the venue phone and the server.
  // Clamped to zero rather than rendered as a future reading, which would be
  // the one thing worse than a stale one: confidently wrong.
  return Math.max(0, Math.floor(ms / 60_000));
}

/**
 * The tier for a datum.
 *
 * In the PLANNING regime this is always "fresh", and that is the rule rather
 * than a simplification: §10 says "no amber, no fading, in this regime". The
 * regime is a parameter so the caller states which surface it is on, and a
 * caller that forgets cannot silently get day-of fading on a planning screen.
 */
export function stalenessTier(
  observedAt: string | Date | null | undefined,
  regime: Regime,
  now: Date = new Date(),
): StalenessTier {
  if (regime === "planning") return "fresh";
  // No observation time means we cannot claim staleness. "Fresh" is wrong and
  // "quiet" is a claim we cannot support, so the caller must not render an "as
  // of" at all — which is what a null observed_at already tells it to do.
  if (!observedAt) return "fresh";

  const mins = minutesSince(observedAt, now);
  if (mins >= DAY_OF_QUIET_MINUTES) return "quiet";
  if (mins >= DAY_OF_AGING_MINUTES) return "aging";
  return "fresh";
}

/*
 * THERE IS NO OPACITY FUNCTION HERE ANY MORE (decision 454). `stalenessOpacityVar`
 * returned `var(--tc-stale-<tier>)` and was the whole mechanism of the fade; the
 * tokens went with it, and so did the `prefers-contrast` and `forced-colors`
 * blocks that existed to neutralise them. Named here so that re-adding a fade is
 * a decision rather than a rediscovery: the tier is still computed, and reaching
 * for an opacity to express it is the exact move that was ruled out.
 */

/**
 * Buckets an elapsed duration for the "as of" label.
 *
 * Returns the i18next key suffix and count, rather than a formatted string,
 * because pluralisation is the locale's business: Russian has three plural
 * forms and Arabic six, and a function returning English text here would make
 * those unreachable (§7).
 */
export function elapsedKey(minutes: number): { key: string; count: number } {
  if (minutes < 1) return { key: "asOf.ago.justNow", count: 0 };
  if (minutes < 60) return { key: "asOf.ago.minutes", count: minutes };

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: "asOf.ago.hours", count: hours };

  return { key: "asOf.ago.days", count: Math.floor(hours / 24) };
}
